import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useSupporterTransferList } from './useSupporterTransferList';
import { useModal } from 'ui/components/screens/modals/useModal';
import { TransferListTable } from 'ui/components/supporter/patterns/TransferList/TransferListTable';
import { BankAccountManagement } from 'ui/components/supporter/patterns/TransferList/BankAccountManagement';
import { TransferApplyModal } from 'ui/components/supporter/patterns/TransferList/TransferApplyModal';
import { SnackBar } from 'ui/components/elements/SnackBar/SnackBar';
import { mqUser } from 'utils/responsive';
import { PageTitle } from 'ui/components/elements/PageTitle/PageTitle';

const BgWrapper = styled.div`
    background-color: var(--supporter-bg);
    height: 100%;
    width: 100%;
    padding: 40px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    overflow-y: auto;
    min-height: calc(100vh - var(--height-pc-header));

    ${mqUser('SP')} {
        padding: 20px 15px;
    }
`;

const Container = styled.div`
    width: 100%;
    max-width: 1200px;
    background-color: var(--white);
    border-radius: 8px;
    padding: 30px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    ${mqUser('SP')} {
        padding: 20px 15px;
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
`;

const BankAccountButton = styled.button`
    background-color: var(--main-color-blue);
    color: var(--white);
    border: none;
    border-radius: 6px;
    padding: 12px 24px;
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: #0056b3;
    }

    ${mqUser('SP')} {
        padding: 10px 16px;
        font-size: var(--font-small);
    }
`;

/**
 * サポーター振込管理ページ
 */
export const SupporterTransferList: React.FC = () => {
    const {
        loading,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        transfers,
        bankAccount,
        saveBankAccount,
        applyTransfer,
    } = useSupporterTransferList();
    const { openModal, closeModal, createModal } = useModal();
    const {
        openModal: openApplyModal,
        closeModal: closeApplyModal,
        createModal: createApplyModal,
    } = useModal();
    const [selectedTransferId, setSelectedTransferId] = useState<number | null>(null);
    const [selectedAmount, setSelectedAmount] = useState<number>(0);

    const handleApplyClick = (transferId: number, amount: number) => {
        setSelectedTransferId(transferId);
        setSelectedAmount(amount);
        openApplyModal();
    };

    const handleApply = async (accountNumber: string) => {
        if (selectedTransferId === null) return;
        await applyTransfer(selectedTransferId, accountNumber);
        closeApplyModal();
        setSelectedTransferId(null);
    };

    const handleCloseApplyModal = () => {
        closeApplyModal();
        setSelectedTransferId(null);
    };

    const bankAccountModalContent = (
        <BankAccountManagement account={bankAccount} onClose={closeModal} onSave={saveBankAccount} loading={loading} />
    );

    const applyModalContent = selectedTransferId !== null && (
        <TransferApplyModal
            transferId={selectedTransferId}
            amount={selectedAmount}
            bankAccount={bankAccount}
            onClose={handleCloseApplyModal}
            onApply={handleApply}
            loading={loading}
        />
    );

    return (
        <BgWrapper>
            <SnackBar message={snackbarMessage} open={showSnackbar} setOpen={setShowSnackbar} />
            {createModal(bankAccountModalContent, {
                keyboard: true,
                maskClosable: true,
                onClose: closeModal,
                scrollable: true,
            })}
            {applyModalContent &&
                createApplyModal(applyModalContent, {
                    keyboard: true,
                    maskClosable: true,
                    onClose: handleCloseApplyModal,
                    scrollable: true,
                })}
            <Container>
                <PageTitle title="振込管理" />
                <Header>
                    <BankAccountButton onClick={openModal}>入金口座情報変更</BankAccountButton>
                </Header>
                <TransferListTable transfers={transfers} onApplyClick={handleApplyClick} />
            </Container>
        </BgWrapper>
    );
};
